import { forwardRef, useImperativeHandle, useRef } from 'react';
import { DateTime } from 'luxon';
import LocationAutocomplete, { type ILocationAutocompleteSelection } from '../LocationAutocomplete';
import { TranslationService } from '../../../services/translation.service';
import { LocationType } from '../../../models/location-type.enum';
import { ClientSideStorageItems } from '../../../services/client-side-storage.service';
import TextFieldFormGroup from '../TextFieldFormGroup';
import ValidationInfo from '../../../validation/validation-info';
import DateTimePicker from '../DateTimePicker';
import type { IServerValidationError } from '../validatable.interface';
import type IValidatable from '../validatable.interface';
import { AutocompleteLabelPosition } from '../AutocompleteBase/autocomplete-base.enum';
import { ActualTripSelectorMode } from './actual-trip-selector-mode.enum';
import { ActualTripSelectorField } from './actual-trip-selector-field.enum';

export interface IActualTripSelectorState {
  value: {
    fromLocation: ILocationAutocompleteSelection | undefined;
    fromDateTime: DateTime;
    toLocation: ILocationAutocompleteSelection | undefined;
    toDateTime: DateTime;
    comments: string | undefined;
    mode: ActualTripSelectorMode;
  };
  validationInfo: ValidationInfo;
}

export interface IActualTripSelectorProps {
  id: string;
  minDateTime?: DateTime;
  maxDateTime?: DateTime;
  showComments?: boolean;
  state: IActualTripSelectorState;
  onChange?:
    | ((value: {
        fromLocation: ILocationAutocompleteSelection | undefined;
        fromDateTime: DateTime;
        toLocation: ILocationAutocompleteSelection | undefined;
        toDateTime: DateTime;
        comments: string | undefined;
        mode: ActualTripSelectorMode;
      }) => void)
    | undefined;
  onKeyUp?: ((field: ActualTripSelectorField) => void) | undefined;
}

export default forwardRef<IValidatable, IActualTripSelectorProps>(function ActualTripSelector(
  { id, minDateTime = DateTime.now().startOf('day').minus({ days: 30 }), maxDateTime = DateTime.now().endOf('day').plus({ days: 30 }), showComments = true, state, onChange, onKeyUp },
  ref
) {
  // === REFERENCES ===
  const fromLocationAutocompleteRef = useRef<IValidatable>(null!);
  const fromDateTimePickerRef = useRef<IValidatable>(null!);
  const toLocationAutocompleteRef = useRef<IValidatable>(null!);
  const toDateTimePickerRef = useRef<IValidatable>(null!);
  const commentsRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      return new ValidationInfo('', [], {
        fromLocation: fromLocationAutocompleteRef.current.checkValidity(),
        fromDateTime: fromDateTimePickerRef.current.checkValidity(),
        toLocation: toLocationAutocompleteRef.current.checkValidity(),
        toDateTime: toDateTimePickerRef.current.checkValidity(),
        comments: showComments ? commentsRef.current.checkValidity() : new ValidationInfo('', []),
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        fromLocation: fromLocationAutocompleteRef.current.handleServerValidationErrors(errors),
        fromDateTime: fromDateTimePickerRef.current.handleServerValidationErrors(errors),
        toLocation: toLocationAutocompleteRef.current.handleServerValidationErrors(errors),
        toDateTime: toDateTimePickerRef.current.handleServerValidationErrors(errors),
        comments: showComments ? commentsRef.current.handleServerValidationErrors(errors) : new ValidationInfo('', []),
      });
    },
  }));

  return (
    <div className="actual-trip-selector">
      <div className={state.value.mode === ActualTripSelectorMode.edit ? '' : 'd-none'}>
        <div className="mb-3">
          <LocationAutocomplete
            /* @ts-expect-error ts(2322) */
            ref={fromLocationAutocompleteRef}
            id={`${id}-from`}
            serverValidationName="actualTripFrom"
            labelText={TranslationService.get('common.from')}
            labelPosition={AutocompleteLabelPosition.above}
            placeholderText={TranslationService.get('common.stop')}
            historyProps={{
              historyLabel: TranslationService.get('common.autocomplete.suggestionlabel.from'),
              localStorageItem: ClientSideStorageItems.LocalStorage.AutocompleteHistoryFrom,
            }}
            locationTypes={[LocationType.stopArea]}
            required={true}
            state={{
              value: state.value.fromLocation,
              validationInfo: state.validationInfo.children.fromLocation ?? new ValidationInfo(),
            }}
            onSelect={(value) => {
              onChange?.({ ...state.value, fromLocation: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(ActualTripSelectorField.fromLocation);
            }}
          />
        </div>
        <div className="mb-5">
          <DateTimePicker
            ref={fromDateTimePickerRef}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            showAsteriskOnRequiredFields={true}
            serverValidationPrefix="actualTripFrom"
            state={{
              value: state.value.fromDateTime,
              validationInfo: state.validationInfo.children.fromDateTime ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onKeyUp?.(ActualTripSelectorField.fromDateTime);
              onChange?.({ ...state.value, fromDateTime: value });
            }}
          />
        </div>
        <div className="mb-3">
          <LocationAutocomplete
            /* @ts-expect-error ts(2322) */
            ref={toLocationAutocompleteRef}
            id={`${id}-to`}
            serverValidationName="actualTripTo"
            labelText={TranslationService.get('common.to')}
            labelPosition={AutocompleteLabelPosition.above}
            placeholderText={TranslationService.get('common.stop')}
            historyProps={{
              historyLabel: TranslationService.get('common.autocomplete.suggestionlabel.to'),
              localStorageItem: ClientSideStorageItems.LocalStorage.AutocompleteHistoryTo,
            }}
            locationTypes={[LocationType.stopArea]}
            required={true}
            state={{
              value: state.value.toLocation,
              validationInfo: state.validationInfo.children.toLocation ?? new ValidationInfo(),
            }}
            onSelect={(value) => {
              onChange?.({ ...state.value, toLocation: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(ActualTripSelectorField.toLocation);
            }}
          />
        </div>
        <div className="mb-3  mb-md-4">
          <DateTimePicker
            ref={toDateTimePickerRef}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            showAsteriskOnRequiredFields={true}
            serverValidationPrefix="actualTripTo"
            state={{
              value: state.value.toDateTime,
              validationInfo: state.validationInfo.children.toDateTime ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onKeyUp?.(ActualTripSelectorField.toDateTime);
              onChange?.({ ...state.value, toDateTime: value });
            }}
          />
        </div>
        {showComments && (
          <TextFieldFormGroup
            ref={commentsRef}
            inputId={`${id}-comment`}
            serverValidationName="actualTripComments"
            labelText={TranslationService.get('common.comments')}
            renderAsTextArea={true}
            rows={7}
            required={showComments}
            state={{
              value: state.value.comments ?? '',
              validationInfo: state.validationInfo.children.comments ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, comments: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(ActualTripSelectorField.comments);
            }}
          />
        )}
      </div>
    </div>
  );
});
